import React, { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import {
  DEFAULT_PREFERENCES,
  OPEN_PREFERENCES_EVENT,
  getStoredConsent,
  initAdsenseFromStoredConsent,
  openCookiePreferences,
  saveConsent,
} from "../../utils/cookieConsent";

const ToggleRow = ({ label, description, checked, onChange, disabled }) => (
  <div className="flex items-start justify-between gap-4 py-3 border-b border-white/10 last:border-b-0">
    <div>
      <p className="text-white font-medium text-sm">{label}</p>
      <p className="text-lightA text-xs mt-1">{description}</p>
    </div>
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      onClick={() => !disabled && onChange(!checked)}
      className={`relative inline-flex items-center h-6 w-11 shrink-0 rounded-full transition-colors ${
        checked ? "bg-customGold" : "bg-customGray"
      } ${disabled ? "opacity-60 cursor-not-allowed" : "cursor-pointer"}`}
    >
      <span
        className={`inline-block w-4 h-4 rounded-full bg-white transform transition ease-in-out duration-200 ${
          checked ? "translate-x-6" : "translate-x-1"
        }`}
      />
    </button>
  </div>
);

const CookieConsent = () => {
  const [bannerVisible, setBannerVisible] = useState(false);
  const [preferencesOpen, setPreferencesOpen] = useState(false);
  const [prefs, setPrefs] = useState(DEFAULT_PREFERENCES);

  useEffect(() => {
    const stored = getStoredConsent();
    if (stored) {
      setPrefs({ ...DEFAULT_PREFERENCES, ...stored.preferences });
      initAdsenseFromStoredConsent();
    } else {
      const timer = setTimeout(() => setBannerVisible(true), 600);
      return () => clearTimeout(timer);
    }
  }, []);

  useEffect(() => {
    const handleOpenPreferences = () => {
      const stored = getStoredConsent();
      if (stored) setPrefs({ ...DEFAULT_PREFERENCES, ...stored.preferences });
      setPreferencesOpen(true);
    };
    window.addEventListener(OPEN_PREFERENCES_EVENT, handleOpenPreferences);
    return () => window.removeEventListener(OPEN_PREFERENCES_EVENT, handleOpenPreferences);
  }, []);

  const closeAll = () => {
    setBannerVisible(false);
    setPreferencesOpen(false);
  };

  const handleAcceptAll = () => {
    saveConsent({ analytics: true, advertising: true });
    closeAll();
  };

  const handleRejectNonEssential = () => {
    saveConsent({ analytics: false, advertising: false });
    closeAll();
  };

  const handleSavePreferences = () => {
    saveConsent(prefs);
    closeAll();
  };

  return (
    <>
      {/* Consent Banner */}
      <AnimatePresence>
        {bannerVisible && !preferencesOpen && (
          <motion.div
            initial={{ y: 100, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 100, opacity: 0 }}
            transition={{ type: "spring", stiffness: 260, damping: 28 }}
            role="dialog"
            aria-label="Cookie consent"
            className="fixed bottom-0 left-0 right-0 z-[100] p-4 sm:p-6"
          >
            <div className="max-w-4xl mx-auto bg-customGray border border-white/10 rounded-2xl shadow-2xl p-5 sm:p-6 flex flex-col sm:flex-row sm:items-center gap-4">
              <div className="flex-1 text-sm text-white/90 leading-relaxed">
                <p className="font-semibold text-white mb-1">We value your privacy</p>
                <p>
                  Blackdefynition uses cookies to keep you signed in and to understand how the Site is used.
                  We also partner with Google AdSense and other vendors that use cookies to serve ads based on
                  your visits to this and other sites. You can accept all cookies, reject non-essential ones, or
                  choose your preferences. Read more in our{" "}
                  <a href="/privacy-policy" className="text-customGold hover:underline">
                    Privacy Policy
                  </a>{" "}
                  and{" "}
                  <a href="/terms" className="text-customGold hover:underline">
                    Terms &amp; Conditions
                  </a>
                  .
                </p>
              </div>
              <div className="flex flex-wrap gap-2 sm:shrink-0">
                <button
                  onClick={() => setPreferencesOpen(true)}
                  className="px-4 py-2 text-sm font-medium rounded-lg border border-white/20 text-white hover:bg-white/10 transition"
                >
                  Manage Preferences
                </button>
                <button
                  onClick={handleRejectNonEssential}
                  className="px-4 py-2 text-sm font-medium rounded-lg border border-white/20 text-white hover:bg-white/10 transition"
                >
                  Reject Non-Essential
                </button>
                <button
                  onClick={handleAcceptAll}
                  className="px-4 py-2 text-sm font-semibold rounded-lg bg-customGold text-customDark hover:brightness-110 transition"
                >
                  Accept All
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Preferences Modal */}
      <AnimatePresence>
        {preferencesOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[110] flex items-center justify-center bg-black/70 p-4"
            onClick={() => setPreferencesOpen(false)}
          >
            <motion.div
              initial={{ opacity: 0, y: 20, scale: 0.97 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 20, scale: 0.97 }}
              transition={{ type: "spring", stiffness: 300, damping: 26 }}
              role="dialog"
              aria-modal="true"
              aria-label="Cookie preferences"
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-md bg-customDark border border-white/10 rounded-2xl shadow-2xl p-6"
            >
              <h2 className="text-lg font-bold text-white mb-1">Cookie Preferences</h2>
              <p className="text-lightA text-xs mb-4">
                Choose which cookies you're comfortable with. You can update these settings at any time from the
                "Cookie Settings" link in the footer. See our{" "}
                <a href="/privacy-policy" className="text-customGold hover:underline">
                  Privacy Policy
                </a>{" "}
                for details.
              </p>

              <div className="divide-y divide-white/10">
                <ToggleRow
                  label="Strictly Necessary"
                  description="Required for login, security, and core site functionality. Always on."
                  checked={true}
                  disabled
                  onChange={() => {}}
                />
                <ToggleRow
                  label="Analytics"
                  description="Helps us understand how the Site is used so we can improve it."
                  checked={prefs.analytics}
                  onChange={(val) => setPrefs((p) => ({ ...p, analytics: val }))}
                />
                <ToggleRow
                  label="Advertising"
                  description="Used by us and vendors like Google AdSense to show ads relevant to you."
                  checked={prefs.advertising}
                  onChange={(val) => setPrefs((p) => ({ ...p, advertising: val }))}
                />
              </div>

              <div className="flex flex-wrap justify-end gap-2 mt-6">
                <button
                  onClick={handleRejectNonEssential}
                  className="px-4 py-2 text-sm font-medium rounded-lg border border-white/20 text-white hover:bg-white/10 transition"
                >
                  Reject Non-Essential
                </button>
                <button
                  onClick={handleSavePreferences}
                  className="px-4 py-2 text-sm font-semibold rounded-lg bg-customGold text-customDark hover:brightness-110 transition"
                >
                  Save Preferences
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export { openCookiePreferences };
export default CookieConsent;
